'use client';

import { format, isValid, parse, set } from 'date-fns';
import { Calendar as CalendarIcon, CalendarClock, Clock } from 'lucide-react';
import * as React from 'react';
import type { Matcher } from 'react-day-picker';

import { Button } from '../../components/button';
import { Calendar } from '../../components/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/popover';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../../components/sheet';
import { useMobileDevice } from '../../hooks/useMediaQuery';
import { cn } from '../../utils';
import { snapToStep } from '../timeClockPicker/snapToStep';
import { TimeClockPanel } from '../timeClockPicker/timeClockPanel';

type Part = 'date' | 'time';
type DateTimeClockPickerParts = 'date-time' | 'time-date' | 'date' | 'time';

const partsOf = (parts: DateTimeClockPickerParts): [Part] | [Part, Part] => {
  if (parts === 'date-time') return ['date', 'time'];
  if (parts === 'time-date') return ['time', 'date'];
  return [parts];
};

const withDay = (date: Date, day: Date) =>
  set(date, { year: day.getFullYear(), month: day.getMonth(), date: day.getDate() });

const parseValue = (value: string | undefined, valueFormat: string) => {
  if (!value) return undefined;
  const parsed = parse(value, valueFormat, new Date());
  return isValid(parsed) ? parsed : undefined;
};

type CommonProps = {
  /** Which parts the field picks, in that order. Default date, then time. */
  parts?: DateTimeClockPickerParts;
  /** Minutes between the minute positions. Default 1. */
  step?: number;
  /** 12 shows 1–12 with AM/PM, 24 shows 0–23 on two rings. Default 12. */
  hourCycle?: 12 | 24;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  id?: string;
  /** Names the picker for assistive tech and titles the phone sheet, e.g. "Start". */
  label: string;
  placeholder?: string;
  /** Merged into the field button. */
  className?: string;
  /**
   * 'compact' draws a smaller clock face and calendar (month and year on one row, smaller days),
   * for a field with little room around it. Default 'default'.
   */
  size?: 'default' | 'compact';
  /**
   * How the picker opens: a bottom sheet, a popover under the field, or (default) a sheet on
   * narrow screens and a popover elsewhere.
   */
  presentation?: 'auto' | 'sheet' | 'popover';
};

type DateValueProps = {
  valueFormat?: undefined;
  value?: Date;
  onChange: (value: Date) => void;
};

type StringValueProps = {
  /**
   * A date-fns format, e.g. `yyyy-MM-dd HH:mm:ss` or `yyyy-MM-dd`: `value` is read in it and
   * `onChange` returns it, instead of a Date. Local time, as `format` and `parse` are.
   */
  valueFormat: string;
  value?: string;
  onChange: (value: string) => void;
};

type DateTimeClockPickerProps = CommonProps & (DateValueProps | StringValueProps);

/**
 * The date and time field: the calendar every date field opens and the clock face, picked in
 * turn in the order `parts` gives, or one of them alone. Picking a day alone commits it; with a
 * time, Done commits and Close, the overlay or Escape discards.
 */
const DateTimeClockPicker = (props: DateTimeClockPickerProps) => {
  const {
    parts = 'date-time',
    step = 1,
    hourCycle = 12,
    minDate,
    maxDate,
    disabled,
    id,
    label,
    placeholder,
    className,
    presentation = 'auto',
    size = 'default',
  } = props;
  const value =
    props.valueFormat === undefined ? props.value : parseValue(props.value, props.valueFormat);
  const emit = (date: Date) => {
    if (props.valueFormat === undefined) props.onChange(date);
    else props.onChange(format(date, props.valueFormat));
  };

  const order = partsOf(parts);
  const hasDate = order.includes('date');
  const hasTime = order.includes('time');
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<Date>(() => snapToStep(value ?? new Date(), step));
  const [part, setPart] = React.useState<Part>(order[0]);
  const isMobile = useMobileDevice();
  const asSheet = presentation === 'sheet' || (presentation === 'auto' && isMobile);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(snapToStep(value ?? new Date(), step));
      setPart(order[0]);
    }
    setOpen(next);
  };

  const commit = (date: Date) => {
    emit(date);
    setOpen(false);
  };

  const timeFormat = hourCycle === 12 ? 'h:mm a' : 'HH:mm';
  const partText = (date: Date, which: Part, dateFormat: string) =>
    format(date, which === 'date' ? dateFormat : timeFormat);

  const Icon = hasDate && hasTime ? CalendarClock : hasDate ? CalendarIcon : Clock;
  const fieldPlaceholder =
    placeholder ??
    (hasDate && hasTime ? 'Pick a date and time' : hasDate ? 'Pick a date' : 'Pick a time');
  const description =
    hasDate && hasTime
      ? `Pick the ${order[0]}, then the ${order[1]}. Tap either one above to change it, then tap Done.`
      : hasDate
        ? 'Tap a day to pick it.'
        : 'Tap or drag the hour on the clock, then the minute, choose AM or PM, then tap Done.';

  const disabledDays: Matcher[] = [];
  if (minDate) disabledDays.push({ before: minDate });
  if (maxDate) disabledDays.push({ after: maxDate });

  const field = (
    <Button
      type="button"
      variant="outline"
      id={id}
      disabled={disabled}
      aria-label={label}
      onClick={asSheet ? () => handleOpenChange(true) : undefined}
      className={cn(
        'touch:min-h-11 touch:text-base w-full justify-start gap-2 px-3 font-normal tabular-nums',
        !value && 'text-muted-foreground',
        className,
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {value
        ? order.map((which) => partText(value, which, 'MMM d, yyyy')).join(' · ')
        : fieldPlaceholder}
    </Button>
  );

  // The phone sheet is full width, so it keeps the full-size picker.
  const compact = size === 'compact' && !asSheet;
  const readout = cn(
    'rounded-md px-3 py-1 font-medium tabular-nums transition-colors',
    compact ? 'text-base' : 'text-lg',
  );

  const picker = (
    <div className={cn('flex flex-col items-center', compact ? 'gap-3' : 'gap-4')}>
      {hasDate && hasTime && (
        <div className="flex items-center gap-2" role="group" aria-label="Date and time">
          {order.map((which) => (
            <button
              key={which}
              type="button"
              aria-pressed={part === which}
              onClick={() => setPart(which)}
              className={cn(
                readout,
                part === which ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
              )}
            >
              {partText(draft, which, 'EEE, MMM d, yyyy')}
            </button>
          ))}
        </div>
      )}
      {part === 'date' ? (
        <Calendar
          className={cn(
            'p-0',
            // The day cells and their buttons are 36px by default; descendant selectors outrank them.
            compact && '[&_td]:h-8 [&_td]:w-8 [&_td_button]:h-8 [&_td_button]:w-8',
          )}
          classNames={
            compact ? { dropdowns: 'flex justify-between px-10 flex-row gap-2 flex-1' } : undefined
          }
          mode="single"
          required
          selected={draft}
          defaultMonth={draft}
          disabled={disabledDays}
          fromYear={minDate ? minDate.getFullYear() : undefined}
          toYear={maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 1}
          onSelect={(day) => {
            const next = withDay(draft, day);
            if (!hasTime) {
              commit(next);
              return;
            }
            setDraft(next);
            if (order[1] === 'time') setPart('time');
          }}
        />
      ) : (
        <TimeClockPanel
          date={draft}
          onChange={setDraft}
          hourCycle={hourCycle}
          step={step}
          size={compact ? 'compact' : 'default'}
          onMinutePicked={order[1] === 'date' ? () => setPart('date') : undefined}
        />
      )}
    </div>
  );

  if (asSheet) {
    return (
      <>
        {field}
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetContent
            side="bottom"
            className="rounded-t-xl pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            closeButtonClassName="hidden"
          >
            <div className="flex items-start justify-between gap-3 pb-2">
              <SheetTitle className="pt-2">{label}</SheetTitle>
              <Button
                type="button"
                variant="ghost"
                className="-mr-3 -mt-1 shrink-0"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </div>
            <SheetDescription className="sr-only">{description}</SheetDescription>
            {picker}
            {hasTime && (
              <Button type="button" className="mt-4 w-full" onClick={() => commit(draft)}>
                Done
              </Button>
            )}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {field}
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('w-auto', compact ? 'p-3' : 'p-4')}>
        {picker}
        {hasTime && (
          <div className="flex justify-end pt-3">
            <Button type="button" size="sm" onClick={() => commit(draft)}>
              Done
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export { DateTimeClockPicker };
export type { DateTimeClockPickerParts, DateTimeClockPickerProps };
